import React, { useState, useRef } from "react";

import clsx from "clsx";

import { pointFrom, type LocalPoint } from "@excalidraw/math";

import { newLinearElement } from "@excalidraw/element";

import { Popover } from "./Popover";
import { ToolButton } from "./ToolButton";

import type { AppClassProperties } from "../types";

const localPoint = (x: number, y: number) => pointFrom(x, y) as LocalPoint;

// Helper to generate points for an arc (sector)
const createArcPoints = (
  radius: number,
  startAngle: number,
  endAngle: number,
) => {
  const points: LocalPoint[] = [];

  // Center
  points.push(localPoint(0, 0));

  const steps = 20;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const angle = startAngle + t * (endAngle - startAngle);
    points.push(localPoint(radius * Math.cos(angle), radius * Math.sin(angle)));
  }
  points.push(localPoint(0, 0)); // Close loop

  // Normalize points to positive coordinates
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  points.forEach(([x, y]) => {
    if (x < minX) {
      minX = x;
    }
    if (y < minY) {
      minY = y;
    }
    if (x > maxX) {
      maxX = x;
    }
    if (y > maxY) {
      maxY = y;
    }
  });

  return points.map(([x, y]) => localPoint(x - minX, y - minY));
};

// Helper to generate star points
const createStarPoints = (
  outerRadius: number,
  innerRadius: number,
  pointsCount = 5,
) => {
  const points: LocalPoint[] = [];
  const cx = outerRadius;
  const cy = outerRadius;

  for (let i = 0; i < pointsCount * 2; i++) {
    const angle = (i * Math.PI) / pointsCount - Math.PI / 2; // Start at top
    const r = i % 2 === 0 ? outerRadius : innerRadius;
    points.push(localPoint(cx + r * Math.cos(angle), cy + r * Math.sin(angle)));
  }
  points.push(points[0]); // Close loop

  // Normalize
  let minX = Infinity;
  let minY = Infinity;
  points.forEach(([x, y]) => {
    if (x < minX) {
      minX = x;
    }
    if (y < minY) {
      minY = y;
    }
  });

  return points.map(([x, y]) => localPoint(x - minX, y - minY));
};

// Define the shapes
const CUSTOM_SHAPES: {
  name: string;
  type: "line";
  points: readonly LocalPoint[];
  width: number;
  height: number;
  icon: React.JSX.Element;
}[] = [
  {
    name: "Triangle",
    type: "line" as const,
    points: [
      localPoint(50, 0),
      localPoint(100, 86),
      localPoint(0, 86),
      localPoint(50, 0),
    ],
    width: 100,
    height: 86,
    icon: (
      <svg
        viewBox="0 0 100 86"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M50 0 L100 86 L0 86 Z" />
      </svg>
    ),
  },
  {
    name: "Hexagon",
    type: "line" as const,
    points: [
      localPoint(25, 0),
      localPoint(75, 0),
      localPoint(100, 43),
      localPoint(75, 86),
      localPoint(25, 86),
      localPoint(0, 43),
      localPoint(25, 0),
    ],
    width: 100,
    height: 86,
    icon: (
      <svg
        viewBox="0 0 100 86"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M25 0 L75 0 L100 43 L75 86 L25 86 L0 43 Z" />
      </svg>
    ),
  },
  {
    name: "Pentagon",
    type: "line" as const,
    points: [
      localPoint(50, 0),
      localPoint(100, 38),
      localPoint(81, 95),
      localPoint(19, 95),
      localPoint(0, 38),
      localPoint(50, 0),
    ],
    width: 100,
    height: 95,
    icon: (
      <svg
        viewBox="0 0 100 95"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M50 0 L100 38 L81 95 L19 95 L0 38 Z" />
      </svg>
    ),
  },
  {
    name: "Trapezoid",
    type: "line" as const,
    points: [
      localPoint(25, 0),
      localPoint(75, 0),
      localPoint(100, 100),
      localPoint(0, 100),
      localPoint(25, 0),
    ],
    width: 100,
    height: 100,
    icon: (
      <svg
        viewBox="0 0 100 100"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M25 0 L75 0 L100 100 L0 100 Z" />
      </svg>
    ),
  },
  {
    name: "Quarter Circle",
    type: "line" as const,
    points: createArcPoints(100, -Math.PI / 2, 0), // Top Right quadrant
    width: 100,
    height: 100,
    icon: (
      <svg
        viewBox="0 0 100 100"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M0 100 L0 0 A 100 100 0 0 1 100 100 Z" />
      </svg>
    ),
  },
  {
    name: "1/3 Circle",
    type: "line" as const,
    points: createArcPoints(
      100,
      -Math.PI / 2,
      -Math.PI / 2 + (2 * Math.PI) / 3,
    ),
    width: 100,
    height: 100,
    icon: (
      <svg
        viewBox="0 0 100 100"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M50 50 L50 10 A 40 40 0 0 1 85 70 Z" />
      </svg>
    ),
  },
  {
    name: "2/3 Circle",
    type: "line" as const,
    points: createArcPoints(
      100,
      -Math.PI / 2,
      -Math.PI / 2 + (4 * Math.PI) / 3,
    ),
    width: 100,
    height: 100,
    icon: (
      <svg
        viewBox="0 0 100 100"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M50 50 L50 10 A 40 40 0 1 1 15 70 Z" />
      </svg>
    ),
  },
  {
    name: "Star",
    type: "line" as const,
    points: createStarPoints(50, 20),
    width: 100,
    height: 100,
    icon: (
      <svg
        viewBox="0 0 100 100"
        width="30"
        height="30"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="50,5 61,35 95,35 68,55 79,85 50,65 21,85 32,55 5,35 39,35" />
      </svg>
    ),
  },
];

interface TemplateButtonProps {
  app: AppClassProperties;
  title?: string;
}

export const TemplateButton: React.FC<TemplateButtonProps> = ({
  app,
  title = "Custom Shapes",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const createShape = (shape: typeof CUSTOM_SHAPES[0]) => {
    const viewportCenter = {
      x: app.state.scrollX + app.state.width / 2,
      y: app.state.scrollY + app.state.height / 2,
    };

    const element = newLinearElement({
      type: shape.type,
      x: viewportCenter.x - shape.width / 2,
      y: viewportCenter.y - shape.height / 2,
      width: shape.width,
      height: shape.height,
      points: shape.points,
      strokeColor: "#000000",
      backgroundColor: "transparent",
      fillStyle: "hachure" as const,
      strokeWidth: 1,
      strokeStyle: "solid" as const,
      roughness: 1,
      opacity: 100,
      roundness: null,
      polygon: true,
    });

    app.onInsertElements([element]);
    setIsOpen(false);
  };

  return (
    <>
      <ToolButton
        ref={buttonRef}
        type="button"
        icon={
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 2L17 6V14L10 18L3 14V6L10 2Z"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <circle cx="10" cy="10" r="2" fill="currentColor" />
          </svg>
        }
        title={title}
        aria-label={title}
        onClick={() => setIsOpen(!isOpen)}
        className={clsx("Shape", { active: isOpen })}
      />
      {isOpen && buttonRef.current && (
        <Popover
          onCloseRequest={() => setIsOpen(false)}
          top={buttonRef.current.offsetTop + buttonRef.current.offsetHeight + 4}
          left={buttonRef.current.offsetLeft}
          fitInViewport
        >
          <div
            className="layer-ui__library"
            style={{
              padding: "12px",
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "8px",
            }}
          >
            {CUSTOM_SHAPES.map((shape, index) => (
              <button
                key={index}
                className="layer-ui__library-item"
                style={{
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid var(--color-gray-30)",
                  borderRadius: "4px",
                  background: "var(--color-gray-10)",
                  cursor: "pointer",
                }}
                onClick={() => createShape(shape)}
                title={shape.name}
              >
                {shape.icon}
              </button>
            ))}
          </div>
        </Popover>
      )}
    </>
  );
};
